/**
 * VDL Sync Service
 *
 * API client for VDL sync history and management
 */

import { apiClient } from './api'

const API_BASE = '/vdl-sync'

// === Types ===

export interface VDLSyncHistory {
  id: number
  project_id: number
  crm_id: number
  sync_type: string
  triggered_by: string | null
  status: string
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
  sources_in_vdl: number | null
  sources_created: number | null
  sources_updated: number | null
  sources_deleted: number | null
  sources_unchanged: number | null
  total_sources: number | null
  active_sources: number | null
  inactive_sources: number | null
  pending_sources: number | null
  pre_approved_sources: number | null
  error_message: string | null
  created_at: string
  project_name: string | null
}

export interface VDLSyncHistoryListResponse {
  items: VDLSyncHistory[]
  total: number
  page: number
  per_page: number
}

export interface VDLSyncConfig {
  auto_sync_enabled: boolean
  auto_sync_interval_minutes: number
  batch_size: number
}

export interface ActiveProject {
  id: number
  crm_id: number
  project_name: string
  status: string
  last_sync_at: string | null
  total_sources: number
  active_sources: number
  pending_sources: number
}

export interface VDLSyncStats {
  total_syncs: number
  completed_syncs: number
  failed_syncs: number
  running_syncs: number
  today_syncs: number
  active_projects_count: number
  auto_sync_enabled: boolean
  auto_sync_interval_minutes: number
  last_sync: {
    project_id: number
    status: string
    started_at: string
  } | null
}

export interface TriggerResponse {
  success: boolean
  message: string
  sync_id?: number
}

// === API Functions ===

/**
 * Get VDL sync history
 */
export async function getSyncHistory(params: {
  project_id?: number
  page?: number
  per_page?: number
}): Promise<VDLSyncHistoryListResponse> {
  const response = await apiClient.get<VDLSyncHistoryListResponse>(`${API_BASE}/history`, {
    params,
  })
  return response.data
}

/**
 * Get VDL sync configuration
 */
export async function getSyncConfig(): Promise<VDLSyncConfig> {
  const response = await apiClient.get<VDLSyncConfig>(`${API_BASE}/config`)
  return response.data
}

/**
 * Get active projects for sync
 */
export async function getActiveProjects(): Promise<ActiveProject[]> {
  const response = await apiClient.get<ActiveProject[]>(`${API_BASE}/active-projects`)
  return response.data
}

/**
 * Get sync statistics
 */
export async function getSyncStats(): Promise<VDLSyncStats> {
  const response = await apiClient.get<VDLSyncStats>(`${API_BASE}/stats`)
  return response.data
}

/**
 * Trigger sync for all active projects
 */
export async function triggerSyncAll(): Promise<TriggerResponse> {
  const response = await apiClient.post<TriggerResponse>(`${API_BASE}/trigger`)
  return response.data
}

/**
 * Trigger sync for a specific project
 */
export async function triggerSyncProject(projectId: number): Promise<TriggerResponse> {
  const response = await apiClient.post<TriggerResponse>(`${API_BASE}/trigger/${projectId}`)
  return response.data
}
